import i18n from '@/utils/i18n';
import PencilIcon from '@assets/icons/pencil.svg';
import React, { useRef } from 'react';
import { Animated, Pressable } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { FulfillmentDropdownTriggerProps } from '@/experiences/sales-portal/domain/ordering/components/fulfillment-dropdown';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useHover } from 'react-native-web-hooks';
import { useInterpolatedAnimatedStyle } from '@/experiences/sales-portal/blocs/hooks/use-interpolated-animated-style';

export const ChangeFulfillmentTrigger: React.FC<
  Pick<FulfillmentDropdownTriggerProps, 'disabled' | 'onPress'>
> = ({ disabled = false, onPress }) => {
  const [styles, theme] = useTriggerStyles();
  const triggerRef = useRef<React.ComponentRef<typeof Pressable>>(null);
  const isHovered = useHover(triggerRef) && !disabled;
  const animatedBackgroundColor = useInterpolatedAnimatedStyle(
    isHovered,
    theme.sk.color.bg.primary.default,
    theme.sk.color.bg.secondary.hover,
  );

  return (
    <Pressable
      accessibilityLabel={i18n.t('sales_portal.checkout_panel.change')}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      ref={triggerRef}
    >
      <Animated.View
        style={[
          styles.changeFulfillmentTrigger,
          {
            backgroundColor: animatedBackgroundColor,
          },
        ]}
      >
        <PencilIcon
          height={12}
          stroke={
            disabled
              ? theme.sk.color.icon.disabled
              : theme.sk.color.icon.primary
          }
          width={12}
        />
        <Typography
          color={disabled ? theme.sk.color.text.disabled : undefined}
          variant="paragraph-md-600"
        >
          {i18n.t('sales_portal.checkout_panel.change')}
        </Typography>
      </Animated.View>
    </Pressable>
  );
};

const useTriggerStyles = createStyles((theme) => ({
  changeFulfillmentTrigger: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusSmall,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    height: 24,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
}));
